/**
 * PRD.330 Phase 2 — Generic report layout.
 *
 *   Title / subtitle
 *   [Date From] [Date To] [Search]            [Clear filters] [Reset report]
 *   Summary: N <countLabel>             <aggregate label> <value> …
 *   Table (sticky header, horizontal overflow, column sort/filter)
 *   Pagination
 *
 * The consumer owns the controller (applied ReportQuery) and executes the
 * query (e.g. `useReportQuery`); the shell only renders the supplied result
 * and emits query changes. Nothing report-specific is hard-coded here.
 */
import { useEffect, type ReactNode } from 'react';
import type { ReportResult } from '@helios/domain';
import { ReportPagination } from './ReportPagination';
import { ReportSummary } from './ReportSummary';
import { ReportTable, type ReportColumnRenderers, type ReportTableMessages } from './ReportTable';
import { ReportToolbar } from './ReportToolbar';
import type { ReportController } from './useReportController';
import { buildSummaryItems, deriveReportViewStatus, reportPageInfo } from './report-view-logic';

export interface ReportShellProps<Row, C extends string> {
  controller: ReportController<C>;
  result: ReportResult<Row, C> & {
    retry?: () => void;
    /** Per facet column: options still loading (see useReportQuery). */
    facetLoading?: Partial<Record<C, boolean>>;
  };
  title: string;
  subtitle?: ReactNode;
  render: ReportColumnRenderers<Row, C>;
  rowKey: (row: Row) => string;
  searchPlaceholder: string;
  countLabel: string;
  /** Display label per aggregate key, e.g. { amountSum: 'Total …' }. */
  aggregateLabels: Readonly<Record<string, string>>;
  formatAggregate: (key: string, value: number) => ReactNode;
  facetLabel?: (columnId: C, value: string) => string;
  summaryNote?: ReactNode;
  /** PRD.330 Phase 4 — optional visualization content, rendered between the summary and the table. */
  visualizations?: ReactNode;
  messages?: Partial<ReportTableMessages>;
}

/** Container-level card sizing for the visualization area (see ReportShell). */
export const VISUALIZATION_LAYOUT_CSS = `
[data-report-visualizations] > :not(style) { flex: 1 1 420px; min-width: 0; }
[data-report-visualizations] > [data-viz-layout="full-row"] { flex-basis: 100%; }
`;

const DEFAULT_MESSAGES: ReportTableMessages = {
  loading: 'Loading…',
  empty: 'No records for this date range.',
  noResults: 'No records match the current search and filters.',
  error: "Couldn't load this report. Try again, or check Sync Status.",
};

export function ReportShell<Row, C extends string>(props: ReportShellProps<Row, C>) {
  const { controller, result } = props;
  const { definition, query } = controller;
  const loading = result.status === 'loading';
  const error = result.status === 'error';
  const status = deriveReportViewStatus({
    query,
    loading,
    error,
    totalCount: result.totalCount,
  });
  const facetLabel = props.facetLabel ?? ((_c: C, v: string) => v);

  // Live updates can shrink the result below the current page — clamp.
  const { clampPage } = controller;
  useEffect(() => {
    if (result.status === 'ready') clampPage(result.totalCount);
  }, [result.status, result.totalCount, clampPage]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-4)',
        height: '100%',
        minHeight: 0,
        padding: 'var(--sp-6)',
        boxSizing: 'border-box',
      }}
    >
      <header>
        <h1
          style={{
            margin: 0,
            fontSize: 'var(--font-size-xl)',
            fontWeight: 'var(--font-weight-semi)',
            color: 'var(--text)',
          }}
        >
          {props.title}
        </h1>
        {props.subtitle && (
          <p
            style={{
              margin: 'var(--sp-1) 0 0',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--text-muted)',
            }}
          >
            {props.subtitle}
          </p>
        )}
      </header>

      <ReportToolbar controller={controller} searchPlaceholder={props.searchPlaceholder} />

      <ReportSummary
        totalCount={result.totalCount}
        countLabel={props.countLabel}
        items={buildSummaryItems(
          definition,
          loading || error ? null : result.aggregates,
          props.aggregateLabels,
        )}
        formatAggregate={props.formatAggregate}
        known={!loading && !error}
        refetching={loading && result.rows.length > 0}
        note={props.summaryNote}
      />

      {props.visualizations && (
        // PRD.330 Phase 5 follow-up — responsive by available width (pure CSS,
        // no viewport listeners): a wrapping flex row where every card is
        // `1 1 420px`, so two cards sit side by side at equal width when each
        // can be ≥ 420px and stack otherwise (a card alone in a row grows to
        // full width). A card marked data-viz-layout="full-row" (e.g. a
        // compact N/A status card) always takes its own full-width row.
        // Cards in a row stretch to the same height.
        <div
          data-report-visualizations
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 'var(--sp-4)',
            alignItems: 'stretch',
            minWidth: 0,
          }}
        >
          <style>{VISUALIZATION_LAYOUT_CSS}</style>
          {props.visualizations}
        </div>
      )}

      <ReportTable
        controller={controller}
        rows={result.rows}
        rowKey={props.rowKey}
        render={props.render}
        status={status}
        refetching={loading && result.rows.length > 0}
        facets={result.facets}
        facetLoading={result.facetLoading ?? {}}
        facetLabel={facetLabel}
        messages={{ ...DEFAULT_MESSAGES, ...props.messages }}
        onRetry={result.retry}
      />

      <ReportPagination
        info={reportPageInfo(query.page, definition.pageSize, error ? 0 : result.totalCount)}
        onPage={controller.setPage}
      />
    </div>
  );
}
